import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Alert,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ReusableTable from "../../../components/ReusableTable/ReusableTable";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import VendorRiskBadge from "../components/VendorRiskBadge";
import useVendorStore from "../store/useVendorStore";
import { styles } from "../styles/Vendors.styles";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "-";

const DiscoveredVendorsTab = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const discovered = useVendorStore((s) => s.discovered);
  const addDiscoveredVendor = useVendorStore((s) => s.addDiscoveredVendor);
  const ignoreDiscoveredVendor = useVendorStore((s) => s.ignoreDiscoveredVendor);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const handleAdd = (item) => {
    addDiscoveredVendor(item.id);
    setSnackbarInfo({ message: `${item.name} added to your vendor register` });
  };

  const handleIgnore = (item) => {
    ignoreDiscoveredVendor(item.id);
    setSnackbarInfo({ message: `${item.name} ignored` });
  };

  const columns = [
    { label: "Vendor", field: "name", align: "left", width: "16%" },
    { label: "Category", field: "category", align: "left", width: "14%" },
    { label: "Discovered From", field: "discoveredFrom", align: "left", width: "16%" },
    { label: "Users", field: "users", align: "left", width: "8%" },
    { label: "Suggested Risk", field: "suggestedRisk", align: "left", width: "12%" },
    { label: "Detected", field: "detectedAt", align: "left", width: "10%" },
    { label: "Actions", field: "actions", align: "right", width: "24%" },
  ];

  const renderCell = (row, column) => {
    switch (column.field) {
      case "name":
        return (
          <Typography variant="body2" sx={{ color: theme.customColors.text.strong, fontWeight: 500 }}>
            {row.name}
          </Typography>
        );
      case "category":
      case "discoveredFrom":
        return (
          <Typography variant="body2" sx={{ color: theme.customColors.text.strong }}>
            {row[column.field]}
          </Typography>
        );
      case "users":
        return <Typography variant="body2">{row.users}</Typography>;
      case "suggestedRisk":
        return <VendorRiskBadge level={row.suggestedRisk} />;
      case "detectedAt":
        return <Typography variant="body2">{formatDate(row.detectedAt)}</Typography>;
      case "actions":
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleAdd(row)}
              sx={{
                ...styles.addButton,
                px: 1.5,
                py: 0.5,
                minWidth: "auto",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Add
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleIgnore(row)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 1.5,
                py: 0.5,
                minWidth: "auto",
                whiteSpace: "nowrap",
                flexShrink: 0,
                color: theme.customColors.text.secondary,
                borderColor: theme.customColors.border.main,
              }}
            >
              Ignore
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />

      <Alert
        severity="info"
        sx={{
          mb: 2,
          backgroundColor: theme.customColors.background.info,
          border: `1px solid ${theme.customColors.border.accent}`,
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ color: theme.customColors.text.strong, mb: 0.5 }}>
          Discovered from your integrations
        </Typography>
        <Typography variant="body2">
          These vendors were automatically detected from connected systems. Add the ones your organization uses, or ignore the rest.{" "}
          <Box
            component="span"
            onClick={() => navigate("/integrations")}
            sx={{
              color: theme.customColors.button.primary,
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Manage integrations
          </Box>
        </Typography>
      </Alert>

      <Card sx={styles.mainCard} variant="outlined">
        <CardHeader
          title={
            <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong}>
              Discovered Vendors
            </Typography>
          }
          sx={styles.cardHeader}
        />
        <CardContent sx={{ p: 0 }}>
          {discovered.length > 0 ? (
            <ReusableTable
              columns={columns}
              data={discovered}
              renderCell={renderCell}
              getCellSx={(_, column) =>
                column.field === "actions" ? { overflow: "visible" } : {}
              }
              showPagination={false}
              height="auto"
            />
          ) : (
            <Box sx={styles.emptyState}>
              <CloudDoneIcon
                sx={{ fontSize: 48, color: theme.customColors.icons.disabled, mb: 3 }}
              />
              <Typography variant="h5" color="textSecondary" sx={{ mb: 2, fontWeight: 600 }}>
                All caught up
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400, lineHeight: 1.5 }}>
                You've triaged every discovered vendor. New vendors will appear here as your integrations detect them.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default DiscoveredVendorsTab;
